/**
 * GAUGE · GRÁFICOS RÁPIDOS
 *
 * Este arquivo é tutorial e lembrete visual. Gauge tem somente três shapes:
 *
 * shape: 1 -> arco de meio círculo, extremidades retas e gaps retos
 * shape: 2 -> arco amplo de 270°, extremidades externas curvas e gaps retos
 * shape: 3 -> progresso circular, extremidades curvas e pista inativa
 *
 * Os três shapes usam a caixa fumê dos gráficos rápidos.
 *
 * Regras já fechadas:
 * - `chartBox: true` é o padrão de dashboard;
 * - `gap` no nível do chart separa módulos do compositor;
 * - `plotOptions.fast.gap` abre cortes retos na escala nos Shapes 1 e 2;
 * - o menu fica na barra superior, ao lado do subtítulo;
 * - a tipografia final do SVG usa `rem` e acompanha o font-size do `html`;
 * - a escala mínima equivale a 0.625rem quando existir escala visível;
 * - `ticks` permite informar explicitamente as marcas da escala;
 * - `tick` continua disponível como atalho para escalas regulares;
 * - `ticks` tem precedência quando os dois forem informados;
 * - cada `band` começa em `from` e termina automaticamente no próximo `from`;
 * - o preenchimento dos Gauges acompanha sempre o valor atual da série;
 * - `unitLayout: 'auto'` põe `%` ao lado do valor, mas mantém a unidade menor;
 * - as demais unidades ficam embaixo e também mantêm tipografia secundária;
 * - `unitLayout` aceita `inline` e `stacked` para forçar apenas o posicionamento.
 *
 * No Shape 3 a escala é opcional. O uso mais comum é percentual, então o
 * tutorial deixa o círculo sem escala. Para exibi-la, use `scale: true` ou
 * informe `ticks: [...]`.
 *
 * Regra do tutorial: opção suportada e relevante que não está ativa no exemplo
 * permanece comentada no ponto em que seria configurada.
 */

export const gaugeShape1 = {
  element: 'chart',
  type: 'gauge',
  shape: 1,
  title: 'Gauge 1',

  // Padrão: true. Use false somente para composição dentro de outro gráfico.
  chartBox: true,
  style: { alignSelf: 'start' },

  plotOptions: {
    fast: {
      min: 0,
      max: 100,

      // No Gauge 1, abre cortes retos exatamente nas divisões principais.
      gap: true,

      // Escala explícita. Se `ticks` existir, ele tem precedência sobre `tick`.
      ticks: [0, 20, 40, 60, 80, 100],
      // tick: 20,

      // tickFontSize: 10,
      // tickColor: '#788493',
      // tickTextColor: '#dce3ea',
      // gapSize: 3,

      // Pista ainda não percorrida e respiro horizontal do chartBox.
      // baseColor: '#3a4652',
      // chartPaddingX: 10,

      // valueColor: '#f5f7fa',
      // unitColor: '#9ca7b7',
      // unitLayout: 'stacked', // auto mostra `78` grande + `%` pequeno inline.

      // Cada faixa vai de `from` até o próximo `from`.
      // A última faixa termina automaticamente em `max`.
      // Neste exemplo, as mudanças de cor coincidem com marcas da escala.
      bands: [
        { from: 0, color: '#d84a3a' },
        { from: 40, color: '#d7a62f' },
        { from: 60, color: '#4ba765' }
      ]
    }
  },

  series: [
    {
      subtitle: 'Atual',
      unit: '%',
      value: 78,
      options: { dataLabels: { format: '{value}', fixed: 0 } }
    }
  ]
};

export const gaugeShape2 = {
  element: 'chart',
  type: 'gauge',
  shape: 2,
  title: 'Gauge 2',

  // Mesma largura final do Gauge 1. A altura maior é definida pelo componente
  // porque o arco de 270° precisa de mais área vertical.
  chartBox: true,
  style: { alignSelf: 'start' },

  plotOptions: {
    fast: {
      min: 0,
      max: 100,
      gap: true,

      ticks: [0, 20, 40, 60, 80, 100],
      // tick: 20,

      // tickFontSize: 10,
      // tickColor: '#788493',
      // tickTextColor: '#dce3ea',
      // gapSize: 3,

      // baseColor: '#2a313a',
      // valueColor: '#f5f7fa',
      // unitColor: '#aab4c1',
      // unitLayout: 'inline', // auto mantém `dias` pequeno embaixo do valor.

      // As bands definem as cores do progresso. O preenchimento para exatamente
      // em `value`; o restante do arco permanece como pista inativa.
      bands: [
        { from: 0, color: '#78bde8' },
        { from: 40, color: '#348dcc' },
        { from: 60, color: '#1a5e91' }
      ]
    }
  },

  series: [
    {
      subtitle: 'Atual',
      unit: 'dias',
      value: 57,
      options: { dataLabels: { format: '{value}', fixed: 0 } }
    }
  ]
};

export const gaugeShape3 = {
  element: 'chart',
  type: 'gauge',
  shape: 3,
  title: 'Gauge 3',

  // Mesmo chartBox de 380px dos outros Gauges. O Shape 3 é propositalmente
  // simples: pista circular + progresso, sem escala no tutorial e sem ponteiro.
  chartBox: true,
  style: { alignSelf: 'start' },

  plotOptions: {
    fast: {
      min: 0,
      max: 100,

      // O Shape 3 aceita escala quando necessário:
      // scale: true,
      // ticks: [0, 20, 40, 60, 80, 100],
      // tick: 20,
      // tickFontSize: 10,
      // tickColor: '#788493',
      // tickTextColor: '#dce3ea',

      // Começa no topo e avança no sentido horário, como um progress ring.
      startAngle: 0,
      thickness: 18,
      baseColor: '#1d2228',

      // valueColor: '#f5f7fa',
      // unitColor: '#9ca7b7',
      // unitLayout: 'stacked', // auto mostra `59` grande + `%` pequeno inline.

      // Uma única band mantém o arco ativo contínuo. A mesma cor alimenta a
      // assinatura lateral porque o accent acompanha a band do valor atual.
      bands: [
        { from: 0, color: '#ff4f70' }
      ]
    }
  },

  series: [
    {
      subtitle: 'Atual',
      unit: '%',
      value: 59,
      options: { dataLabels: { format: '{value}', fixed: 0 } }
    }
  ]
};

/**
 * Uma única lista visível no tutorial. Nada de família/skin/tamanho em seções
 * separadas: são apenas três exemplos pequenos de Gauge em Gráficos rápidos.
 */
export const gaugeQuickCharts = [
  gaugeShape1,
  gaugeShape2,
  gaugeShape3
];

// Alias temporário para imports antigos do tutorial.
export const gaugeCatalog = gaugeQuickCharts;